import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { client } from "@/lib/api";
import { RivetsApiError, type ClaimResponse } from "@rivets/core";
import { BrandLogo } from "@/components/BrandLogo";
import { SkeletonRows } from "@/components/Skeleton";
import { useInitialFocus } from "@/hooks/useInitialFocus";

export const CLAIM_TOKEN_STORAGE_KEY = "rivets:claimToken";

type State =
  | { status: "loading" }
  | { status: "manual"; error?: string }
  | { status: "error"; message: string }
  | { status: "ok"; data: ClaimResponse };

export function ClaimPage() {
  const [state, setState] = useState<State>({ status: "loading" });
  const [manualToken, setManualToken] = useState("");
  const tokenRef = useInitialFocus<HTMLInputElement>(state.status === "manual");

  useEffect(() => {
    const sessionId = new URLSearchParams(window.location.search).get("session_id");
    if (!sessionId) {
      // No Stripe redirect — offer manual entry (support-issued or re-entered tokens).
      setState({ status: "manual" });
      return;
    }
    client
      .claim(sessionId)
      .then((data) => {
        sessionStorage.setItem(CLAIM_TOKEN_STORAGE_KEY, data.claimToken);
        setState({ status: "ok", data });
      })
      .catch((e) => {
        setState({
          status: "error",
          message: e instanceof RivetsApiError ? e.message : "Could not verify your purchase.",
        });
      });
  }, []);

  function submitManualToken(e: React.FormEvent) {
    e.preventDefault();
    const token = manualToken.trim();
    if (!token) {
      setState({ status: "manual", error: "Enter your claim token." });
      return;
    }
    sessionStorage.setItem(CLAIM_TOKEN_STORAGE_KEY, token);
    window.location.assign("/register");
  }

  return (
    <div className="auth-shell">
      <div className="auth-card claim-card">
        <BrandLogo className="auth-logo" size={22} />
        <h1 className="auth-title">Claim your license</h1>

        {state.status === "loading" && (
          <div className="panel">
            <SkeletonRows rows={3} />
          </div>
        )}

        {state.status === "manual" && (
          <form className="panel" onSubmit={submitManualToken}>
            <p className="auth-subtitle" style={{ marginTop: 0, marginBottom: 10 }}>
              Just completed checkout? Use the link from your Stripe receipt. Otherwise, paste
              your claim token below.
            </p>
            <label className="label" htmlFor="claim-token">
              Claim token
            </label>
            <input
              id="claim-token"
              ref={tokenRef}
              className="input mono"
              value={manualToken}
              onChange={(e) => setManualToken(e.target.value)}
              placeholder="claim_…"
            />
            {state.error && <p className="error-text">{state.error}</p>}
            <button className="btn btn-primary btn-block" style={{ marginTop: 14 }} type="submit">
              Continue
            </button>
            <Link className="btn btn-ghost btn-block" style={{ marginTop: 8 }} to="/buy">
              Back to purchase
            </Link>
          </form>
        )}

        {state.status === "error" && (
          <div className="panel">
            <p className="error-text" style={{ marginTop: 0 }}>
              {state.message}
            </p>
            <button className="btn btn-block" onClick={() => setState({ status: "manual" })}>
              Enter a claim token manually
            </button>
            <Link className="btn btn-ghost btn-block" style={{ marginTop: 8 }} to="/buy">
              Back to purchase
            </Link>
          </div>
        )}

        {state.status === "ok" && (
          <div className="panel claim-success">
            <div className="auth-state-icon" aria-hidden="true">✓</div>
            <p className="auth-subtitle" style={{ marginBottom: 10 }}>
              {state.data.seats} seats purchased for {state.data.purchaseEmail}.
            </p>
            <div className="label">Claim token</div>
            <div className="token-box mono">{state.data.claimToken}</div>
            <Link className="btn btn-primary btn-block" style={{ marginTop: 14 }} to="/register">
              Create your account
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
